import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { Link } from 'src/components/ui/link';
import { appRoutes } from 'src/routes';
import {
  BREADCRUMB_ANCESTOR_COLLAPSED,
  BREADCRUMB_SEPARATOR_COLLAPSED,
  Breadcrumb,
  BreadcrumbHeading,
  BreadcrumbHeadingSeparator,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/components/ui/breadcrumb';
import { PageHeadingSlot } from 'src/components/ui/layouts/authenticated-layout/page-heading-slot';
import { RequestIdBadge } from 'src/components/request-id-badge/request-id-badge';
import { defaultSortFilter } from 'src/api/services/RequestClient';

type Props = React.PropsWithChildren<{
  /** Rendered as the page's h1. */
  title: string;
  /** Appended to the h1 as a badge. Omit on states where no request id exists yet. */
  requestId?: string;
  companyId?: string;
  /**
   * The company crumb is skipped when this is missing. The route's Suspense and error states
   * render outside `CompanyQueryProvider` and so cannot know the name — omitting the crumb is
   * right there, where a placeholder would read as a crumb literally labelled "unset".
   */
  companyName?: string;
  /**
   * Renders a trailing crumb linking back to the parent request. Only pass it when the parent
   * actually exists — an empty id generates a path with a blank `:requestId` segment, which
   * matches no route.
   */
  parentRequestId?: string;
  /** Label for the parent crumb. An outcome's parent is a project; a revision's is a request. */
  parentLabel?: string;
  /** Trailing crumb for a sub-page of the request, e.g. Revisions. */
  currentCrumb?: React.ReactNode;
}>;

/**
 * The heading every `/request/:requestId` state renders into the sticky header (#2611).
 *
 * All four `flow` branches plus the route's loading and error states share this, so the trail
 * shape and the h1 cannot drift between them — before this existed the outcome branch had
 * already lost its company crumb.
 *
 * `children` land beside the h1, for per-flow extras such as a status cell.
 */
export const RequestPageHeading: React.FC<Props> = ({
  title,
  requestId,
  companyId,
  companyName,
  parentRequestId,
  parentLabel,
  currentCrumb,
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const requestsListPath = companyId
    ? {
        pathname: generatePath(appRoutes.companyRequestsList, { companyId }),
        search: defaultSortFilter,
      }
    : undefined;

  const parentRequestPath =
    parentRequestId && companyId
      ? generatePath(appRoutes.request, { requestId: parentRequestId, companyId })
      : undefined;

  const goBack = () => {
    // Ordered from most to least specific. `parentRequestPath` is the destination the parent
    // crumb already points at, so the chevron and that crumb cannot drift apart: it is what
    // returns the revisions page to its request and an outcome to its project when there is no
    // history state — the behaviour both had before this component existed.
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }

    if (parentRequestPath) {
      navigate(parentRequestPath, { state: location.state });
      return;
    }

    if (requestsListPath) {
      navigate(requestsListPath, { state: location.state });
      return;
    }

    navigate(appRoutes.companiesList);
  };

  return (
    <PageHeadingSlot>
      <Breadcrumb>
        <BreadcrumbList>
          {/* Nothing in this crumb can shrink, so it opts out of the row's shrinking rather
              than collapsing under its own button. */}
          <BreadcrumbItem className={'tw-shrink-0'}>
            <Button variant={'unset'} size={'iconXs'} onClick={goBack}>
              <ChevronLeft className={'tw-size-4'} />
            </Button>
          </BreadcrumbItem>

          {/* Ancestors collapse below md — see BREADCRUMB_ANCESTOR_COLLAPSED. The current crumb
              and the h1 below stay at every width, so the header still identifies the page. */}
          <BreadcrumbItem className={BREADCRUMB_ANCESTOR_COLLAPSED}>
            <BreadcrumbLink asChild>
              <Link to={appRoutes.companiesList}>{t('Companies')}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>

          {!!companyId && !!companyName && (
            <>
              <BreadcrumbSeparator className={BREADCRUMB_SEPARATOR_COLLAPSED} />
              <BreadcrumbItem className={BREADCRUMB_ANCESTOR_COLLAPSED}>
                <BreadcrumbLink asChild>
                  <Link to={generatePath(appRoutes.company, { companyId })}>{companyName}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}

          {!!requestsListPath && (
            <>
              <BreadcrumbSeparator className={BREADCRUMB_SEPARATOR_COLLAPSED} />
              <BreadcrumbItem className={BREADCRUMB_ANCESTOR_COLLAPSED}>
                <BreadcrumbLink asChild>
                  <Link to={requestsListPath} state={location.state}>
                    {t('Requests')}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}

          {!!parentRequestPath && (
            <>
              <BreadcrumbSeparator className={BREADCRUMB_SEPARATOR_COLLAPSED} />
              <BreadcrumbItem className={BREADCRUMB_ANCESTOR_COLLAPSED}>
                <BreadcrumbLink asChild>
                  <Link to={parentRequestPath} state={location.state}>
                    {parentLabel ?? t('Project')}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}

          {!!currentCrumb && (
            <>
              <BreadcrumbSeparator className={BREADCRUMB_SEPARATOR_COLLAPSED} />
              {/* No collapse class: this crumb is the only thing naming the sub-page, so hiding
                  it below md would make the revisions header identical to the request one and
                  leave the page with no aria-current element at all. Its separator still
                  collapses — with the ancestors gone there is nothing in front of it. */}
              <BreadcrumbItem>
                {/* BreadcrumbPage, not a bare node: it carries aria-current="page" and the
                    truncate that every other crumb has, so a long sub-page label ellipsizes
                    instead of being clipped mid-glyph by the header's overflow-hidden. */}
                <BreadcrumbPage>{currentCrumb}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      {/* Below md this only separates something from the h1 when the current crumb is showing;
          with the ancestors collapsed there is otherwise nothing to its left but the chevron. */}
      <BreadcrumbHeadingSeparator
        className={currentCrumb ? undefined : BREADCRUMB_SEPARATOR_COLLAPSED}
      />

      {/* The request title is unique per record, so it moves into the header row with the
          breadcrumb rather than being dropped with the large page titles (#2611). */}
      <BreadcrumbHeading className={'tw-flex tw-items-center tw-gap-2'}>
        <span className={'tw-truncate'}>{title}</span>
        {!!requestId && <RequestIdBadge id={requestId} />}
      </BreadcrumbHeading>

      {children}
    </PageHeadingSlot>
  );
};
